import { useEffect, useState } from 'react';
import Lucide from '@/components/Base/Lucide';
import Button from '@/components/Base/Button';
import {
  addTestMobileNumber,
  deleteTestMobileNumber,
  fetchTestMobileNumbers,
  type TestMobileNumber,
} from '@/api/siteSettings';

const inputClass =
  'block w-full rounded-[0.5rem] border border-slate-200/80 dark:border-darkmode-400 bg-white dark:bg-darkmode-600 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 shadow-sm focus:border-primary focus:ring-4 focus:ring-primary/20';

function nextSubmissionLabel(completedCount: number): string {
  if (completedCount >= 4) return 'Limit reached (4 of 4)';
  const next = completedCount + 1;
  const ordinal = ['1st', '2nd', '3rd', '4th'][next - 1] ?? `${next}th`;
  const set = next === 1 ? 'Set A' : 'Set B';
  return `Next: ${ordinal} submission (${set})`;
}

export default function TestMobileNumbersCard() {
  const [numbers, setNumbers] = useState<TestMobileNumber[]>([]);
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadNumbers = async () => {
    const rows = await fetchTestMobileNumbers();
    setNumbers(rows);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    void fetchTestMobileNumbers()
      .then((rows) => {
        if (!cancelled) setNumbers(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load test mobile numbers');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAdd = async () => {
    setError('');
    setSuccess('');
    if (!mobile.trim()) {
      setError('Enter a mobile number');
      return;
    }
    setAdding(true);
    try {
      await addTestMobileNumber(mobile);
      setMobile('');
      await loadNumbers();
      setSuccess('Mobile number added');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add mobile number');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (row: TestMobileNumber) => {
    setError('');
    setSuccess('');
    setDeletingId(row.id);
    try {
      await deleteTestMobileNumber(row.id);
      setNumbers((current) => current.filter((item) => item.id !== row.id));
      setSuccess(`${row.display} removed`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete mobile number');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="box rounded-xl border border-slate-200/60 dark:border-darkmode-400 px-6 py-5 shadow-sm">
      <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
        Mobile Number for Testing Purposes
      </h2>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        These numbers can walk through the survey without saving answers to the customer database.
        OTP SMS is still sent. TNG PIN is TEST 12345 (stock PINs are not used). The 1st–4th
        submission sequence still applies. Delete a number to start again from the 1st submission.
      </p>

      <form
        className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          void handleAdd();
        }}
      >
        <div className="min-w-0 flex-1">
          <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Mobile number
          </label>
          <input
            type="tel"
            inputMode="tel"
            value={mobile}
            onChange={(e) => {
              setMobile(e.target.value);
              setSuccess('');
            }}
            className={inputClass}
            placeholder="012-345 6789"
            disabled={loading || adding}
          />
        </div>
        <Button type="submit" variant="primary" disabled={loading || adding} className="shrink-0">
          {adding ? (
            <>
              <Lucide icon="Loader" className="stroke-[1.3] w-4 h-4 mr-2 animate-spin" />
              Adding…
            </>
          ) : (
            <>
              <Lucide icon="Plus" className="stroke-[1.3] w-4 h-4 mr-2" />
              Add
            </>
          )}
        </Button>
      </form>

      {error ? (
        <div className="mt-4 px-4 py-3 rounded-lg bg-danger/10 text-danger text-sm border border-danger/20">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="mt-4 px-4 py-3 rounded-lg bg-emerald-50 text-emerald-800 text-sm border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:border-emerald-800">
          {success}
        </div>
      ) : null}

      <div className="mt-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Lucide icon="Loader" className="w-4 h-4 animate-spin" />
            Loading numbers…
          </div>
        ) : numbers.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">No test mobile numbers yet.</p>
        ) : (
          <ul className="divide-y divide-slate-200/80 dark:divide-darkmode-400 rounded-lg border border-slate-200/80 dark:border-darkmode-400 overflow-hidden">
            {numbers.map((row) => (
              <li
                key={row.id}
                className="flex items-center justify-between gap-3 bg-white dark:bg-darkmode-600 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 dark:text-slate-100">{row.display}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {nextSubmissionLabel(row.completedCount)}
                  </p>
                </div>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md p-2 text-slate-400 hover:bg-danger/10 hover:text-danger transition-colors disabled:opacity-50"
                  aria-label={`Delete ${row.display}`}
                  disabled={deletingId === row.id}
                  onClick={() => void handleDelete(row)}
                >
                  {deletingId === row.id ? (
                    <Lucide icon="Loader" className="w-4 h-4 animate-spin" />
                  ) : (
                    <Lucide icon="Trash2" className="w-4 h-4" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
